
import React, { useMemo } from 'react';
import { Cloud, Globe, Layers, Loader2 } from 'lucide-react';
import { ChunkStatus } from '../../client-merge';
import { ChunkGrid } from './ChunkGrid';

interface DetailedExportProgressProps {
    chunks: (ChunkStatus & { mode?: 'local' | 'server' })[];
    totalDuration?: number; // seconds
    fps?: number; // default 30
    isMerging?: boolean;
    onRetryChunk?: (index: number) => void;
}

export const DetailedExportProgress: React.FC<DetailedExportProgressProps> = ({
    chunks,
    totalDuration = 0,
    fps = 30,
    isMerging = false,
    onRetryChunk
}) => {
    // Stats Computation
    const stats = useMemo(() => {
        // Cloud Stats
        const serverChunks = chunks.filter(c => c.mode === 'server');
        const completedServerChunks = serverChunks.filter(c => c.serverStatus === 'complete').length;
        const serverProgressSum = serverChunks.reduce((s, c) => s + (c.serverStatus === 'complete' ? 100 : c.serverProgress), 0);
        const serverGlobalProgress = serverChunks.length > 0 ? Math.round(serverProgressSum / serverChunks.length) : 0;

        // Parallel tasks (processing or encoding)
        const parallelCount = serverChunks.filter(c =>
            c.serverStatus === 'processing' || c.serverStatus === 'encoding'
        ).length;

        // Web (Local) Stats
        const localChunks = chunks.filter(c => c.mode === 'local');
        // For local chunks, "localProgress" tracks the *encoding* progress if we wired it correctly
        // Or if it's a server chunk, localProgress tracks *download*.
        // We only care about "Web" progress for actual local chunks here.
        const localProgressSum = localChunks.reduce((s, c) => s + c.localProgress, 0);
        const localGlobalProgress = localChunks.length > 0 ? Math.round(localProgressSum / localChunks.length) : 0;

        // Frame Computation (Approximate)
        const totalFrames = Math.round(totalDuration * fps);
        // Estimate processed frames based on chunk progress
        // Each chunk represents approx totalFrames / chunks.length (simplified)
        // Better: we don't know exact duration of each chunk here easily without passing it.
        // Let's use simple average.
        const globalAvgProgress = chunks.length > 0 ?
            (serverProgressSum + localProgressSum) / chunks.length : 0; // Mixing 0-100 scales

        // Wait, localChunks progress is 0-100. Server is 0-100. 
        // But we need to account that server chunks also need to be downloaded (stage 2).
        // Let's simplify: "Frames" is just an estimation for the cool factor.
        const processedFrames = Math.round(totalFrames * (globalAvgProgress / 100));

        return {
            serverGlobalProgress,
            localGlobalProgress,
            parallelCount,
            processedFrames,
            totalFrames,
            completedChunks: chunks.filter(c => c.localStatus === 'downloaded').length, // fully ready
            totalChunks: chunks.length
        };

    }, [chunks, totalDuration, fps]);

    return (
        <div className="bg-gray-900 text-white rounded-xl p-4 shadow-xl border border-gray-700 font-mono text-xs">
            {/* Header / Stats Line */}
            <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 text-gray-400">
                        <Layers className="w-4 h-4 text-purple-400" />
                        <span className="font-bold text-white">
                            {stats.completedChunks}/{stats.totalChunks}
                        </span>
                        <span>chunks</span>
                    </div>

                    {stats.parallelCount > 0 && (
                        <div className="flex items-center gap-1.5 text-blue-400 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            <span>{stats.parallelCount}x parallel</span>
                        </div>
                    )}
                </div>

                <div className="text-gray-400">
                    <span className="text-white font-bold">{stats.processedFrames}</span>
                    <span className="opacity-50">/{stats.totalFrames} frames</span>
                    {/* FPS would be cool but we don't have it explicitly yet */}
                </div>
            </div>

            {/* Progress Bars */}
            <div className="space-y-4 mb-4">
                {/* Cloud Progress */}
                <div>
                    <div className="flex justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <Cloud className="w-3.5 h-3.5 text-blue-400" />
                            <span className="font-bold">Cloud (MP4)</span>
                        </div>
                        <span className="font-bold text-blue-400">{stats.serverGlobalProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 transition-all duration-300 ease-out"
                            style={{ width: `${stats.serverGlobalProgress}%` }}
                        />
                    </div>
                </div>

                {/* Web (Local) Progress */}
                <div>
                    <div className="flex justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5 text-purple-400" />
                            <span className="font-bold">Web (MP4)</span>
                        </div>
                        <span className="font-bold text-purple-400">{stats.localGlobalProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-purple-600 to-fuchsia-500 transition-all duration-300 ease-out"
                            style={{ width: `${stats.localGlobalProgress}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Assemblage Status */}
            {isMerging && (
                <div className="flex items-center gap-2 text-green-400 mb-3 animate-pulse">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="font-bold">Assemblage local (FFmpeg.wasm)...</span>
                </div>
            )}

            {/* Grid Visualization (Reusing ChunkGrid but styling it for dark mode context?) 
                Actually ChunkGrid has hardcoded bg-colors. We might want to adjust ChunkGrid to be theme-aware 
                or just accept that it's colorful. 
            */}
            <div className="pt-3 border-t border-gray-700">
                <ChunkGrid chunks={chunks} onRetryChunk={onRetryChunk} />
            </div>
        </div>
    );
};
